import React, { useEffect } from "react";
import { Switch, Route } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import { connect } from "react-redux";
import Dashboard from "./components/dashboard/Dashboard";
import "./assets/styles/index.scss";
//import ErrorBoundary from "./components/common/ErrorBoundary";
import ErrorBoundaryFallback from "./components/common/ErrorBoundaryFallback";
import { SnackbarProvider } from "notistack";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Notifier from "./components/common/Notifier";
import SignIn from "./components/login/SignIn";
import NewPassword from "./components/login/NewPassword";
import UnsignedRoute from "./components/common/UnsignedRoute";
import { setUser, setUserLoaded, login, logout } from "./store/actions/auth";
import jwt from "jsonwebtoken";
import API, { API_NO_TOKEN } from "./utils/axios";
import { setCookie, getCookie, eraseCookie } from "./utils/cookies";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import * as Sentry from "@sentry/react";

import { enqueueSnackbar } from "./store/actions/snackbars";
import { setBillingDetails } from "./store/actions/account";
import { LANDING_URL, STRIPE_SECRET_KEY } from "./components/data/constants";
import { resetProject } from "./store/actions/project";
import ResetPassword from "./components/login/ResetPassword";
import history from "./utils/history";
import { checkIsFOJW } from "./utils/helpers";

const stripePromise = loadStripe(STRIPE_SECRET_KEY, {
  locale: "en"
});
const useStyles = makeStyles(theme => ({
  root: {
    maxHeight: "100vh",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default
  },
  dismissSnackbar: {
    "& svg": {
      color: "rgba(255, 255, 255, 0.56)"
    },

    "&:hover svg": {
      color: "#fff"
    }
  },
  snackbarRoot: {
    color: "#fff !important"
  },
  info: {
    backgroundColor: theme.palette.primary.main
  }
}));

function App({
  setUser,
  setUserLoaded,
  setBillingDetails,
  userLoaded,
  userData,
  notes,
  resetProject,
  login,
  logout,
  enqueueSnackbar
}) {
  const classes = useStyles();

  // add action to all snackbars
  const notistackRef = React.createRef();
  const onClickDismiss = key => () => {
    notistackRef.current.closeSnackbar(key);
  };

  const isFOJW = checkIsFOJW();

  const fetchProfileAndSetUserLoaded = async () => {
    try {
      const profile = await API.get("v1/users/profile/");
      if (getCookie("accessToken")) {
        setUser(profile.data);
        setUserLoaded(true);
        if (
          profile.data &&
          profile.data.organisationSite.organisation
            .subscription_last_payment_status === "processing"
        ) {
          setTimeout(fetchProfileAndSetUserLoaded, 60000);
        } else {
          const billingDetails = await API.get("/v1/billing/details");
          setUser(profile.data);
          setBillingDetails(billingDetails.data);
          setUserLoaded(true);
        }
      }
    } catch (e) {
      if (e && e.status === 404) {
        eraseCookie("accessToken");
        eraseCookie("expiresAt");
        eraseCookie("refreshToken");

        const isAllowedToLogout =
          window.location.pathname !== "/login/" &&
          window.location.pathname !== "/reset-password/" &&
          window.location.pathname !== "/new-password/";

        if (isAllowedToLogout) {
          if (isFOJW || process.env.NODE_ENV === "development") {
            history.push("/login");
          } else {
            window.location.href = `${LANDING_URL}/login`;
          }
        }
        setUserLoaded(true);
        enqueueSnackbar({ message: "No user found" });
      }
    }
  };

  // Create freemium user
  useEffect(() => {
    const expiresAt = getCookie("expiresAt");
    if (
      getCookie("accessToken") &&
      new Date(+expiresAt || expiresAt) > new Date()
    ) {
      fetchProfileAndSetUserLoaded();
    } else if (getCookie("refreshToken")) {
      API_NO_TOKEN.post("/v1/token/refresh", {
        token: getCookie("refreshToken")
      })
        .then(res => {
          const token = "Bearer " + res.data.accessToken;
          setCookie("accessToken", token, 365);
          setCookie("refreshToken", res.data.refreshToken || "", 365);
          setCookie("expiresAt", res.data.expiresAt, 365);
          API.defaults.headers.common["Authorization"] =
            getCookie("accessToken");

          fetchProfileAndSetUserLoaded();
        })
        .catch(res => {
          eraseCookie("accessToken");
          eraseCookie("expiresAt");
          eraseCookie("refreshToken");
          setUserLoaded(true);
        });
    } else {
      setUserLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser]);

  useEffect(() => {
    const checkLogin = () => {
      if (
        API.defaults.headers.common["Authorization"] &&
        getCookie("accessToken") &&
        API.defaults.headers.common["Authorization"] !==
          getCookie("accessToken")
      ) {
        const cookieToken = getCookie("accessToken").slice(7);
        const apiToken = API.defaults.headers.common["Authorization"].slice(7);
        const cookieDecoded = jwt.decode(cookieToken, { complete: true });
        const apiDecoded = jwt.decode(apiToken, { complete: true });
        if (
          cookieDecoded &&
          apiDecoded &&
          cookieDecoded.payload?.id === apiDecoded.payload?.id
        ) {
          API.defaults.headers.common["Authorization"] =
            "Bearer " + cookieToken;
        } else {
          if (getCookie("accessToken")) {
            window.location.reload();
          }
        }
      }
    };
    window.addEventListener("focus", checkLogin);
    return () => {
      window.removeEventListener("focus", checkLogin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSignedIn = userLoaded && userData && userData.id;

  return (
    <div className={classes.root}>
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={3000}
        ref={notistackRef}
        hideIconVariant
        domRoot={document.getElementById("react-notification")}
        className={classes.snackbarRoot}
        classes={{
          variantInfo: classes.info,
          containerAnchorOriginTopCenter: "info-snackbar-wrapper"
        }}
        action={key => (
          <IconButton
            onClick={onClickDismiss(key)}
            className={classes.dismissSnackbar}
          >
            <CloseIcon />
          </IconButton>
        )}
      >
        <Notifier />
        <Sentry.ErrorBoundary
          fallback={fallbackProps => (
            <ErrorBoundaryFallback {...fallbackProps} />
          )}
        >
          <Elements stripe={stripePromise}>
            <Switch>
              {/* Login routes */}

              <UnsignedRoute
                path="/new-password/"
                component={NewPassword}
                exact
                isLoggedIn={isSignedIn}
              />
              <UnsignedRoute
                path="/reset-password/"
                component={ResetPassword}
                exact
                isLoggedIn={isSignedIn}
              />
              <UnsignedRoute
                path="/login/"
                component={SignIn}
                isLoggedIn={isSignedIn}
                exact
              />
              <Route path="/" component={Dashboard} />
            </Switch>
          </Elements>
        </Sentry.ErrorBoundary>
      </SnackbarProvider>
    </div>
  );
}

const mapStateToProps = state => ({
  notes: state.snackbar.notifications,
  userLoaded: state.auth.userLoaded,
  userData: state.auth.userData
});

export default connect(mapStateToProps, {
  setUser,
  login,
  setUserLoaded,
  logout,
  resetProject,
  setBillingDetails,
  enqueueSnackbar
})(App);
